import React from 'react';
import PropTypes from 'prop-types';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import {FirstPage as FirstPageIcon, LastPage as LastPageIcon, MoreHoriz as MoreHorizIcon} from '@material-ui/icons';
import {createUltimatePagination} from 'react-ultimate-pagination';
import {isPlainObject} from 'lodash/lang';
import {calcTableEntriesInfo} from 'utils/common';

class KluwellTable extends React.Component {
  static propTypes = {
    TableCore: PropTypes.func.isRequired,
    recordPages: PropTypes.object.isRequired,
    fetchRecords: PropTypes.func.isRequired,
    setCurrentRecordPage: PropTypes.func.isRequired,
    setCurrentRecord: PropTypes.func,
    clearRecordsAfterPage: PropTypes.func.isRequired,
    refreshTableKey: PropTypes.number,
    isHardRefresh: PropTypes.bool,
    searchBoxPlaceholder: PropTypes.string,
    ExtraFilters: PropTypes.func,
    extraFiltersEnabled: PropTypes.bool.isRequired,
    extraFilterProps: PropTypes.object,
    restProps: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      itemsPerPage: 50,
      filterValue: '',
      sortOpts: {},
      extraFilters: {},
      refreshTableKey: null,
    };
    this.handlePageSelect = this.handlePageSelect.bind(this);
    this.handleRecordsPerPageSelect = this.handleRecordsPerPageSelect.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleSortableFieldClick = this.handleSortableFieldClick.bind(this);
    this.handleRefreshTable = this.handleRefreshTable.bind(this);
    this.handleExtraFiltersChange = this.handleExtraFiltersChange.bind(this);
    this.extraFiltersRef = null;
  }

  componentDidMount() {
    if (this.extraFiltersRef && this.extraFiltersRef.defaultValues) {
      this.handleExtraFiltersChange(this.extraFiltersRef.defaultValues);
    } else {
      this.handleRefreshTable(true);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {refreshTableKey, isHardRefresh} = nextProps;
    if (refreshTableKey && refreshTableKey !== 0 && refreshTableKey !== this.state.refreshTableKey) {
      this.setState(
        {
          refreshTableKey,
        },
        () => this.handleRefreshTable(isHardRefresh === true),
      );
    }
  }

  componentWillUnmount() {
    const {clearRecordsAfterPage} = this.props;
    clearRecordsAfterPage(0);
  }

  handlePageSelect(page) {
    const {recordPages} = this.props;
    if (page === recordPages.currentPage) {
      return;
    }
    this.setState(
      {
        page,
      },
      () => {
        if (recordPages.hasOwnProperty(page)) {
          this.props.setCurrentRecordPage(page);
        } else {
          this.handleRefreshTable();
        }
      },
    );
  }

  handleRecordsPerPageSelect({target: {value}}) {
    if (value === this.state.itemsPerPage) {
      return;
    }
    this.setState(
      {
        itemsPerPage: value,
      },
      () => this.handleRefreshTable(true),
    );
  }

  handleFilterChange(e) {
    const filterValue = e.target.value;
    this.setState(
      {
        filterValue,
      },
      () => this.handleRefreshTable(true),
    );
  }

  handleSortableFieldClick(fieldName, sortDir) {
    let sortOpts = {};
    if (sortDir) {
      sortOpts = {
        sortField: fieldName,
        sortDir,
      };
    }
    this.setState(
      {
        sortOpts,
      },
      () => this.handleRefreshTable(true),
    );
  }

  handleRefreshTable(hardRefresh = false) {
    const {fetchRecords, clearRecordsAfterPage} = this.props;
    const {page: currentPage, itemsPerPage, filterValue, sortOpts, extraFilters} = this.state;
    const page = hardRefresh ? 1 : currentPage;
    return fetchRecords(filterValue, page, itemsPerPage, sortOpts, extraFilters).then(() =>
      clearRecordsAfterPage(page),
    );
  }

  handleExtraFiltersChange(e) {
    let extraFilters;
    if (
      Array.isArray(e) &&
      e.reduce(
        (isListOfObject, obj) => isListOfObject && obj.hasOwnProperty('name') && obj.hasOwnProperty('value'),
        true,
      )
    ) {
      extraFilters = e.reduce((filters, obj) => {
        filters[obj.name] = obj.value;
        return filters;
      }, {});
    } else if (e.hasOwnProperty('name') && e.hasOwnProperty('value')) {
      extraFilters = {
        [e.name]: e.value,
      };
    } else if (isPlainObject(e)) {
      extraFilters = e;
    } else {
      extraFilters = {
        [e.target.name]: e.target.value,
      };
    }
    this.setState(
      (prevState) => ({
        extraFilters: {
          ...prevState.extraFilters,
          ...extraFilters,
        },
      }),
      () => this.handleRefreshTable(true),
    );
  }

  render() {
    const {
      recordPages,
      setCurrentRecord,
      TableCore,
      ExtraFilters,
      searchBoxPlaceholder,
      extraFiltersEnabled,
      extraFilterProps = {},
      restProps,
    } = this.props;
    const {itemsPerPage, filterValue, extraFilters} = this.state;
    return (
      <div className="kluwell-table">
        <div className="filters">
          <div className="common-filters">
            <div className="filter item-per-page">
              <span>Show: </span>
              <TextField select value={itemsPerPage} onChange={this.handleRecordsPerPageSelect}>
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </TextField>
            </div>
            <div className="filter search">
              <TextField
                type="text"
                placeholder={searchBoxPlaceholder || 'Search'}
                value={filterValue}
                onChange={this.handleFilterChange}
              />
            </div>
          </div>
          {ExtraFilters && extraFiltersEnabled && (
            <ExtraFilters
              ref={(ref) => (this.extraFiltersRef = ref)}
              values={extraFilters}
              onChange={this.handleExtraFiltersChange}
              {...extraFilterProps}
            />
          )}
        </div>
        <div className="table">
          <TableCore
            {...{
              recordPages,
              setCurrentRecord,
              onSortableFieldClick: this.handleSortableFieldClick,
              refreshTable: this.handleRefreshTable,
              ...restProps,
            }}
          />
        </div>
        <div className="table-footer">
          <span className="records-details">
            {calcTableEntriesInfo(itemsPerPage, recordPages.currentPage, recordPages.totalCount)}
          </span>
          {React.createElement(
            createUltimatePagination({
              itemTypeToComponent: {
                PAGE: ({value, isActive, isDisabled, onClick}) => (
                  <Button color={isActive ? 'primary' : 'default'} disabled={isDisabled} onClick={onClick}>
                    {value}
                  </Button>
                ),
                ELLIPSIS: ({isDisabled, onClick}) => (
                  <Button disabled={isDisabled} onClick={onClick}>
                    <MoreHorizIcon />
                  </Button>
                ),
                FIRST_PAGE_LINK: ({isDisabled, onClick}) => (
                  <Button disabled={isDisabled} onClick={onClick}>
                    <FirstPageIcon />
                  </Button>
                ),
                LAST_PAGE_LINK: ({isDisabled, onClick}) => (
                  <Button disabled={isDisabled} onClick={onClick}>
                    <LastPageIcon />
                  </Button>
                ),
              },
              WrapperComponent: (props) => <div className="pagination">{props.children}</div>,
            }),
            {
              currentPage: recordPages.currentPage,
              totalPages: recordPages.pageCount,
              hidePreviousAndNextPageLinks: true,
              onChange: this.handlePageSelect,
            },
          )}
        </div>
      </div>
    );
  }
}

const withKluwellTableWrapper = (TableCore, ExtraFilters) => {
  class KluwellTableWrapper extends React.Component {
    static propTypes = {
      recordPages: PropTypes.object.isRequired,
      fetchRecords: PropTypes.func.isRequired,
      setCurrentRecordPage: PropTypes.func.isRequired,
      setCurrentRecord: PropTypes.func,
      clearRecordsAfterPage: PropTypes.func.isRequired,
      refreshTableKey: PropTypes.number,
      isHardRefresh: PropTypes.bool,
      searchBoxPlaceholder: PropTypes.string,
      TableCore: PropTypes.func,
      extraFiltersEnabled: PropTypes.bool.isRequired,
      extraFilterProps: PropTypes.object,
    };
    static defaultProps = {
      extraFiltersEnabled: false,
    };

    render() {
      const {
        recordPages,
        fetchRecords,
        setCurrentRecordPage,
        setCurrentRecord,
        clearRecordsAfterPage,
        refreshTableKey,
        isHardRefresh,
        searchBoxPlaceholder,
        extraFiltersEnabled,
        extraFilterProps,
        ...restProps
      } = this.props;
      return (
        <KluwellTable
          {...{
            recordPages,
            fetchRecords,
            setCurrentRecordPage,
            setCurrentRecord,
            clearRecordsAfterPage,
            refreshTableKey,
            isHardRefresh,
            searchBoxPlaceholder,
            restProps,
            TableCore,
            extraFiltersEnabled,
            extraFilterProps,
            ExtraFilters,
          }}
        />
      );
    }
  }

  return KluwellTableWrapper;
};

export default withKluwellTableWrapper;
